/**
 * FR5 — Brief stage (mock 08): the video brief is first-class and editable;
 * regenerate-with-comment re-runs ONLY the storyboard agent (plan mode).
 */

import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import type { AdCreativeVideoBrief } from '@segmently/schema-models/ad-creative';
import { StyledTextField } from '@modules/ui-components';
import { AD_CREATIVE_STORYBOARD } from '../../selectors';
import { INSIGHT_CATEGORY_COLOR } from '../../services/AdCreativeInsightContextClient';
import type { ThesisOption } from './StoryboardWorkspace';

const SB = AD_CREATIVE_STORYBOARD;
const DURATION_CHOICES = [10, 15, 20, 24, 30] as const;
const BRIEF_ROLES = ['hook', 'problem', 'demo', 'proof', 'cta'] as const;

interface StoryboardBriefStageProps {
  brief?: AdCreativeVideoBrief;
  insightMapping?: AdCreativeVideoBrief['insightMapping'];
  /** FR7: names of the batch brief's chosen creative frameworks. */
  frameworkNames?: string[];
  thesisOptions: ThesisOption[];
  hasScenes: boolean;
  running: boolean;
  busy: string | null;
  onSaveBrief: (patch: { concept?: string; styleLock?: string; negativePrompt?: string; targetDurationSec?: number }) => Promise<boolean>;
  onRegenerate: (instruction: string) => Promise<boolean>;
  onApprove: () => void;
}

export function StoryboardBriefStage({
  brief,
  insightMapping,
  frameworkNames = [],
  thesisOptions,
  hasScenes,
  running,
  busy,
  onSaveBrief,
  onRegenerate,
  onApprove,
}: StoryboardBriefStageProps): React.ReactElement {
  const [concept, setConcept] = useState(brief?.concept ?? '');
  const [styleLock, setStyleLock] = useState(brief?.styleLock ?? '');
  const [negativePrompt, setNegativePrompt] = useState(brief?.negativePrompt ?? '');
  const [targetDurationSec, setTargetDurationSec] = useState<number>(brief?.targetDurationSec ?? 20);
  const [instruction, setInstruction] = useState('');
  const [durationAnchor, setDurationAnchor] = useState<HTMLElement | null>(null);

  // Follow the persisted brief when a regeneration lands a new version.
  useEffect(() => {
    setConcept(brief?.concept ?? '');
    setStyleLock(brief?.styleLock ?? '');
    setNegativePrompt(brief?.negativePrompt ?? '');
    if (brief?.targetDurationSec) setTargetDurationSec(brief.targetDurationSec);
  }, [brief?.version, brief?.concept, brief?.styleLock, brief?.negativePrompt, brief?.targetDurationSec]);

  const dirty = concept !== (brief?.concept ?? '')
    || styleLock !== (brief?.styleLock ?? '')
    || negativePrompt !== (brief?.negativePrompt ?? '')
    || targetDurationSec !== (brief?.targetDurationSec ?? 20);

  const saveIfDirty = async (): Promise<boolean> => {
    if (!dirty) return true;
    // Dirty save needs a concept only when creating the very first brief by
    // hand — edits of secondary fields alone can't create a concept-less v1.
    if (!concept.trim() && !brief) return false;
    return await onSaveBrief({
      concept: concept.trim() || undefined,
      styleLock,
      negativePrompt,
      targetDurationSec,
    });
  };

  return (
    <Box data-testid={SB.briefStage}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Left: editable brief fields */}
        <Stack spacing={1.5} sx={{ flex: 1.2, minWidth: 0, width: '100%', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main', mr: 0.75 }} />
            Video brief
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              derived from insights + angle · editable
            </Typography>
            {/* FR7: chosen framework(s) — the scene grid seeds from their beats. */}
            {frameworkNames.map((name) => (
              <Chip
                key={name}
                size="small"
                color="primary"
                variant="outlined"
                data-testid={SB.briefFramework}
                label={`Framework: ${name}`}
                sx={{ ml: 1, height: 20, fontSize: 11 }}
              />
            ))}
          </Typography>
          <StyledTextField
            data-testid={SB.briefField('concept')}
            label="Concept / hook"
            value={concept}
            onChange={setConcept}
            multiline
            minRows={3}
            fullWidth
            disabled={running}
          />
          <StyledTextField
            data-testid={SB.briefField('styleLock')}
            label="Style lock (applies to every scene)"
            value={styleLock}
            onChange={setStyleLock}
            multiline
            minRows={2}
            fullWidth
            disabled={running}
          />
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <StyledTextField
              data-testid={SB.briefField('negativePrompt')}
              label="Negative prompt"
              value={negativePrompt}
              onChange={setNegativePrompt}
              fullWidth
              disabled={running}
            />
            <Button
              data-testid={SB.briefField('duration')}
              variant="outlined"
              size="small"
              disabled={running}
              onClick={(event) => setDurationAnchor(event.currentTarget)}
              sx={{ whiteSpace: 'nowrap', mt: 1 }}
            >
              {targetDurationSec}s ▾
            </Button>
            <Menu open={Boolean(durationAnchor)} anchorEl={durationAnchor} onClose={() => setDurationAnchor(null)}>
              {DURATION_CHOICES.map((seconds) => (
                <MenuItem
                  key={seconds}
                  selected={seconds === targetDurationSec}
                  onClick={() => {
                    setTargetDurationSec(seconds);
                    setDurationAnchor(null);
                  }}
                >
                  {seconds}s
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>

        {/* Right: grounding panel (role → thesis) */}
        <Stack spacing={1} data-testid={SB.groundingPanel} sx={{ flex: 1, minWidth: 0, width: '100%', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            🧩 Insight grounding
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              scene role ← thesis
            </Typography>
          </Typography>
          {BRIEF_ROLES.map((role) => {
            const entry = (insightMapping ?? []).find((mapping) => mapping.role === role);
            const option = entry?.sourcePath
              ? thesisOptions.find((candidate) => candidate.sourcePath === entry.sourcePath)
              : undefined;
            const category = option?.category ?? entry?.sourcePath?.match(/\]\.([a-zA-Z]+)\[/)?.[1];
            return (
              <Stack key={role} direction="row" spacing={1} alignItems="center" data-testid={SB.groundingRow(role)}>
                <Typography variant="caption" sx={{ width: 72, fontWeight: 700, textTransform: 'uppercase', flexShrink: 0 }}>
                  {role}
                </Typography>
                {role === 'cta' ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    no source needed — brand close
                  </Typography>
                ) : entry?.label || entry?.sourcePath ? (
                  <>
                    {category && (
                      <Chip size="small" color={INSIGHT_CATEGORY_COLOR[category] ?? 'default'} label={category} sx={{ height: 18, fontSize: 10 }} />
                    )}
                    <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                      {entry.label ?? entry.sourcePath}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    auto-mapped at generation
                  </Typography>
                )}
              </Stack>
            );
          })}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Only theses from your selection are offered. Unsourced proof scenes stay brand-generic (claim-safety).
          </Typography>
        </Stack>
      </Stack>

      {/* Bottom bar: instruction + regenerate + approve */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
        <StyledTextField
          data-testid={SB.briefInstruction}
          placeholder="Instruction for regeneration — e.g. “make the hook harsher”…"
          value={instruction}
          onChange={setInstruction}
          size="small"
          disabled={running}
          sx={{ flex: '1 1 320px', minWidth: 260 }}
        />
        <Button
          variant="outlined"
          size="small"
          data-testid={SB.regenerateBrief}
          // Extra input is NEVER required: with no brief yet the storyboard
          // agent derives the concept from the item's copy + insights.
          disabled={running || busy !== null}
          onClick={() => {
            void (async () => {
              // First-brief case without a concept: fields can't persist yet —
              // thread them into the agent instruction instead (never lost).
              const firstBriefWithoutConcept = dirty && !concept.trim() && !brief;
              const effectiveInstruction = firstBriefWithoutConcept
                ? [instruction.trim(), styleLock.trim() && `Style lock: ${styleLock.trim()}`, negativePrompt.trim() && `Negative prompt: ${negativePrompt.trim()}`]
                  .filter(Boolean).join('\n')
                : instruction;
              const saved = firstBriefWithoutConcept ? true : await saveIfDirty();
              if (saved) {
                const ok = await onRegenerate(effectiveInstruction);
                if (ok) setInstruction('');
              }
            })();
          }}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {brief ? '↻ Regenerate brief' : '✨ Generate brief + scenes'} · text
        </Button>
        <Button
          variant="contained"
          size="small"
          data-testid={SB.approveBrief}
          disabled={running || busy !== null || !hasScenes}
          onClick={() => {
            void (async () => {
              const saved = await saveIfDirty();
              if (saved) onApprove();
            })();
          }}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Approve → Scenes
        </Button>
        <Typography variant="caption" color="text.secondary" data-testid={SB.briefVersion} sx={{ whiteSpace: 'nowrap' }}>
          {brief ? `v${brief.version} · previous versions kept` : 'no brief yet'}
        </Typography>
      </Stack>
    </Box>
  );
}
